// Turn-phase runner, extracted from GameBoard.jsx. Phase metadata
// (which steps run in Start / Main / End) lives in
// engine/core/turnPhases.js; runPhaseStep is the dispatch switch that
// maps each step id onto its host-side handler. Adding a new phase
// rule stays a two-edit change: one metadata entry there, one case
// here. passTurn rotates currentTurn/turnNumbers and fires the END
// then START phases across the boundary.
//
// Host contract: state (currentTurn, turnNumbers, gameState), meshes,
// effectLedger, playerResources, sync, _applyTap, prompts
// (_openPrompt/_pushStorylineEvents), pile/draw primitives, logAction.

import { UI } from '../../../utils/arena/uiSounds';
import * as authCardState from './authCardState';
import { resolveAvatarRules } from '../../../utils/game/avatarRules';
import { updateLifeHUD } from '../../../utils/game/cardMesh';
import { OP, applyAnimateAuraTransform, consumeSkipNextUntap, revertAnimateAuraTransform } from '../../../utils/game/engine/automation';
import { SILENT_OP_SET, dispatchSilentOp } from '../../../utils/game/engine/automation/ops/silentOps';
import { clearThresholdWaiver } from '../../../utils/game/engine/automation/ops/spellcasterGrant';
import { openScryTopForController } from './automationRuntime';

import { PHASE, PHASE_STEPS, clearProvidesThisTurn, clearSummoningSickness, consumeSkipNextTurn, dropDrawPhaseChoices, expireFreshCharge } from '../../../utils/game/engine/core';
import { tickLifespansAtEndOfTurn } from '../../../utils/game/engine/core/auraDuration';
import { isCardSilenced } from '../../../utils/game/engine/core/keywordQuery';
import { isDisabled } from '../../../utils/game/engine/automation';
import { playSound } from '../../../utils/game/sounds';
import { isAuthMatchEnabled } from '../../../utils/game/net/authMatchFlag';
import { sendMatchCommand } from '../../../utils/game/socketClient';
import { PASS_TURN, SEED_PHASE } from '../../../utils/game/net/matchCore';
import { healIntent } from '../../../utils/game/net/turnIntents';
import { serverOwnsTurnTriggerEntry, triggerIntent } from '../../../utils/game/net/automationIntents';
import EffectPromptDrawer from '../EffectPromptDrawer';
import * as hotkeys from '../controllers/hotkeys';
import { toast } from 'sonner';
import { t } from '../../../localization/t';
import { cardLogLabel, turnBanner } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';

// Return the active local-controller draw_phase_choice prompt, or
// null. Used to (a) render the pile-anchored draw buttons and
// (b) gate the other draw paths (pile click / drag-out / hotkey)
// until the player resolves the mandatory start-phase draw.
export function localDrawPhaseChoicePrompt(host) {
  if (host._authZonesEnabled?.()) return serverDrawPhaseChoicePrompt(host);
  const prompts = host.state.pendingPrompts || [];
  if (prompts.length === 0) return null;
  // v1.110. Pile-anchored draw buttons only render when the draw is
  // the SOLE storyline prompt. When other effects share the stack
  // (Seer trigger, queued Genesis effects, etc.) the prompt instead
  // routes through the EffectPromptDrawer + StorylinePile so the
  // player sees the explicit stack order with drop-shadowed cards
  // resolving one at a time.
  if (prompts.length !== 1) return null;
  const localPlayer = host.props.isCasualSandbox
    ? (host.state.activePlayerNum === 2 ? 'p2' : 'p1')
    : (host.state.isHost ? 'p1' : 'p2');
  const match = prompts[0];
  if (match.promptType !== 'draw_phase_choice' || match.controller !== localPlayer) return null;
  return match;
}

export const SERVER_DRAW_PROMPT_PREFIX = 'server-draw-';

// Auth: the start-phase draw prompt is a pure function of the server's
// `phase.drawPending` (valkenhall-qa#158). It lives outside pendingPrompts, so
// no other prompt can stack over it, and it survives a Full because the
// server carries it. `drawPromptSent` hides it from the DRAW command until the
// next fold (a double-click must not draw twice); applyTurnPhaseFromMirror
// releases it there, so a DRAW the server dropped brings the prompt back.
function serverDrawPhaseChoicePrompt(host) {
  const pending = host.state.drawPending;
  const player = pending?.seat;
  if (!player || player !== host._localSeat()) return null;
  const id = `${SERVER_DRAW_PROMPT_PREFIX}${player}-g${pending.globalTurn}`;
  if (host.state.drawPromptSent === id) return null;
  return { id, controller: player, promptType: 'draw_phase_choice', ...drawPromptPiles(host, player) };
}

// Resolve the server draw prompt: one DRAW command, rendered from the fold.
// playerDrawFromPile owns the chokes (offline, empty pile, Garden of Eden cap)
// and reports whether it sent the DRAW; a declined draw leaves the prompt up.
export function confirmServerDrawPrompt(host, promptId, choiceValue) {
  const prompt = serverDrawPhaseChoicePrompt(host);
  if (!prompt || prompt.id !== promptId) return;
  if (choiceValue !== 'spellbook' && choiceValue !== 'atlas') return;
  if (choiceValue === 'atlas' && prompt._singleDraw) return;
  const pileId = choiceValue === 'atlas' ? prompt._atlasPileId : prompt._spellbookPileId;
  if (host.playerDrawFromPile(pileId)) host.setState({ drawPromptSent: promptId });
}

// The two draw decks belonging to `player`. Rotation is the ownership axis:
// p2's piles render rotated.
function ownDrawPiles(host, player) {
  const ownerRotated = player === 'p2';
  const piles = host.state.gameState?.piles || [];
  return {
    spellbook: piles.find((p) => p.name === 'Spellbook' && !!p.rotated === ownerRotated),
    atlas:     piles.find((p) => p.name === 'Atlas'     && !!p.rotated === ownerRotated),
  };
}

function drawPromptPiles(host, player) {
  const { spellbook, atlas } = ownDrawPiles(host, player);
  const avatar = host._findAvatarForPlayer(player === 'p2' ? 2 : 1);
  const forbidAtlas = !!resolveAvatarRules(avatar).deckbuilding?.forbidAtlas;
  return {
    _spellbookPileId: spellbook?.id || null,
    _atlasPileId:     forbidAtlas ? null : (atlas?.id || null),
    _singleDraw:      forbidAtlas,
  };
}

// v1.38. Sorcery turn structure orchestrator.
//
// Each phase is a sequence of step descriptors declared in
// cardEffects/turnPhases.js. _runPhaseStep is the single dispatch
// point: step.id → imperative helper. To add a new rule, add the
// step to the metadata AND a case here. There is no third place
// to chase down.
export function runPhaseSteps(host, phase, player) {
  const steps = PHASE_STEPS[phase] || [];
  for (const step of steps) {
    host._runPhaseStep(phase, step, player);
    // A skipped turn short-circuits the rest of its START steps: no
    // untap, mana, draw, or start triggers. `_skipTurnPassPending`
    // stays set for the caller to fire the re-pass after the turn
    // switch commits.
    if (host._skipTurnPassPending) break;
  }
}

export function runPhaseStep(host, phase, step, player) {
  switch (step.id) {
    // ── Start phase steps ────────────────────────────────────
    case 'consume_skip_next_turn': {
      // Auth: the vendored PASS_TURN reducer consumes the flag server-side
      // (pass-through. The turn never flips to a flagged player, and the
      // skipped turn does not increment their turnNumber). Never re-consume
      // off a stale folded flag; the solo/off-auth path below is unchanged.
      if (host._authZonesEnabled?.()) break;
      const avatar = host._findAvatarForPlayer(player === 'p2' ? 2 : 1);
      if (avatar && consumeSkipNextTurn(avatar)) {
        const who = avatar.name || (player === 'p1' ? msg('Player 1') : msg('Player 2'));
        host.logAction('{name} skips this turn', { name: who });
        // Signal runPhaseSteps to abandon the remaining START steps.
        // The actual pass is fired by the caller AFTER the turn switch
        // commits (completePassTurn / the turn:pass handler), so the
        // re-pass reads currentTurn as the skipped player, not the one
        // who just passed.
        host._skipTurnPassPending = true;
      }
      break;
    }
    case 'end_until_next_turn':
      // Ledger "until next turn" entries are pruned at END (see
      // stepPruneExpiredEffects). A no-op here, kept for codex
      // fidelity. This step DOES own the Enchantress animate-aura
      // identity revert: it must fire on the caster's own client at
      // the start of their next turn, the one client that holds the
      // revert entry.
      host._stepEndUntilNextTurn(player);
      break;
    case 'untap_all':
      host._stepUntapAll(player);
      break;
    case 'provide_mana':
      host._stepProvideMana(player);
      break;
    case 'fire_on_turn_start':
      host._stepFireTriggers(step.trigger, player);
      break;
    case 'draw_card':
      host._stepDrawCard(player);
      break;
    // ── End phase steps ──────────────────────────────────────
    case 'fire_on_turn_end':
      host._stepFireTriggers(step.trigger, player);
      break;
    case 'expire_turn_lifespans': {
      // Decrement every "Lasts N of your turns" counter on this
      // player's auras; dispel any that hit 0. Engine module owns
      // the counter math; GameBoard just routes the returned cards
      // through the silent-ops host's cemetery sink.
      const expired = tickLifespansAtEndOfTurn(host.meshes, player);
      for (const card of expired) {
        host._silentOpsHost.logAction('{card} expires after running its turns', { card: cardLogLabel(card, msg('Aura')) });
        host._silentOpsHost.sendToCemetery(card);
      }
      if (expired.length > 0) host.recomputeStaticAuras();
      break;
    }
    case 'heal_minions':
      host._stepHealMinions(player);
      break;
    case 'end_this_turn_effects':
      host._stepPruneExpiredEffects(player);
      break;
    case 'lose_unused_mana':
      // Codex: any remaining mana is lost. Currently implicit.
      // mana is RESET (not decremented) by 'provide_mana' on the
      // next turn's Start phase, so unused mana is overwritten.
      // Step kept for codex fidelity; future spells that ref
      // "mana lost at end of turn" can hook in here.
      break;
    case 'fire_after_each_turn':
      host._stepFireTriggers(step.trigger, player);
      break;
    default:
      console.warn('[turn] unknown phase step id:', step.id);
  }
}

// ── Phase-step implementations ─────────────────────────────────
// Each is a thin wrapper extracted from the legacy passTurn body.
// Keep behaviour-preserving; new rule tweaks go inside these.
export function stepUntapAll(host, player) {
  // Reset the per-turn draw counter for the player whose turn is
  // beginning. Runs in the START phase BEFORE the mandatory draw
  // step, so the start-phase draw increments it from 0. Iron Maiden
  // reads this counter ("lose life equal to cards drawn this turn").
  if (!host._cardsDrawnThisTurn) host._cardsDrawnThisTurn = { p1: 0, p2: 0 };
  host._cardsDrawnThisTurn[player] = 0;
  // v1.116. Garden of Eden ("Players can't draw more than one spell
  // each turn") reads a SPELL-only draw counter (Spellbook draws, not
  // Atlas). Reset it for the player whose turn begins, mirroring the
  // all-draw counter above.
  if (!host._spellDrawsThisTurn) host._spellDrawsThisTurn = { p1: 0, p2: 0 };
  host._spellDrawsThisTurn[player] = 0;
  // Codex: "All of your cards untap." Sites are intentionally
  // skipped (players untap sites manually via T / action menu).
  // Also clears summoning sickness on every minion on the board:
  // sickness lasts "until the end of turn" the minion entered, so
  // any card still flagged at this boundary just shed it.
  // On the auth path the SERVER untaps inside PASS_TURN, atomically with the
  // flip, and the fold has rendered that diff (and logged any Waveshaper skip
  // off the flag flip) before this phase runs, since socketListeners defers
  // START behind the fold's setState. So the loop below keeps only the badge
  // bookkeeping; it neither sends an untap intent nor untaps locally (qa#114:
  // both re-untapped a minion whose one-shot skip PASS_TURN had consumed). Off
  // the auth path it stays the legacy per-card broadcast.
  const auth = !!host._authZonesEnabled?.();
  const playerRotated = player === 'p2';
  for (const [, mesh] of host.meshes) {
    const card = mesh.userData.cardInstance;
    if (!card || card.isSite) continue;
    // Sickness lasts "until the end of turn" (codex). The turn the minion
    // ENTERED, whoever controls it. Clear it for BOTH players' cards at the
    // boundary so last turn's summons can defend on this turn (server
    // parity: clearThisTurnEffects). Untap below stays owner-scoped.
    const wasSick = !!card._hasSummoningSickness;
    clearSummoningSickness(card);
    // Sickness renders as a status badge, so a card that just shed it needs a
    // rebuild (same reason the Charge sunset below rebuilds). Both seats'
    // cards clear here, so both seats' badges refresh.
    if (wasSick) host.rebuildStatusBadges(card.id);
    if (card.rotated !== playerRotated) continue;
    if (!auth) {
      // v1.103. Waveshaper's "They don't untap the next time they
      // would." Pure helper consumes the one-shot flag.
      if (consumeSkipNextUntap(card)) {
        host.logAction('{name} skips untap (Waveshaper)', { name: cardLogLabel(card, msg('A minion')) });
        continue;
      }
      host._applyTap(card, mesh, false, { source: 'end_turn', log: false, broadcast: true });
    }
    // Charge sunset (per codex it only ever matters on the summon
    // turn). Mutates _printedKeywords; rebuild the on-mesh badges
    // so the CHG pill stops showing for cards it no longer affects.
    if (card._chargeFresh) {
      expireFreshCharge(card);
      host.rebuildStatusBadges(card.id);
    }
  }
}

export function stepProvideMana(host, player) {
  // Auth path (W3): the SERVER refills the incoming player's mana inside the
  // PASS_TURN commit (refillIncomingMana. Sites + ledger, after the boundary
  // prune) and the resources fold renders it. Computing + asserting SET_MANA
  // here made the client a second writer. Sandbox / legacy keep the local step.
  if (host._authZonesEnabled?.()) return;
  // Codex: "All your sites and other mana sources provide their
  // mana for this turn." Reset = sites in play + ledger grants
  // (e.g. Black Obelisk +2). Overwrites mid-turn spending and
  // accounts for sites gained or lost since last turn.
  const manaCount = host.countSitesForPlayer(player) + host.effectLedger.manaContribution(player);
  host.playerResources.setMana(player, manaCount);
  host.recalculateSiteAffinities(player);
}

export function stepFireTriggers(host, trigger, player) {
  // Walk every card the player controls and enqueue prompts for
  // any matching trigger entries. Equipped artifacts on each host
  // also fire (Black Obelisk attached to a site is the canonical
  // case). Unknown triggers (e.g. 'after_each_turn' before parser
  // support) silently iterate to nothing. ApplyCardAutomation
  // gates on entry.trigger matching.
  // v1.98. Entries whose effects are ALL silent-resolvable ops
  // (OPEN_SCRY_TOP today) bypass the prompt drawer and fire inline.
  // The scry dialog IS the player interaction, so a separate
  // confirm prompt would be redundant.
  if (!trigger) return;
  const playerRotated = player === 'p2';
  // `after_each_turn` fires on BOTH players' cards (Cursed Iron:
  // "at the end of each turn"). Every other turn trigger
  // (on_turn_start, on_turn_end) stays controller-scoped.
  const crossController = trigger === 'after_each_turn';
  // Silent ops fire inline at turn start/end without a confirm prompt.
  // The card's text reads as automatic resolution ("destroy <self>"
  // gives the player no choice), so a Confirm would be pure friction.
  // The engine's silentOps module owns the full set; this superset
  // adds OPEN_SCRY_TOP which is GameBoard-resolved (opens a UI dialog).
  const SILENT_OPS = new Set([...SILENT_OP_SET, OP.OPEN_SCRY_TOP]);
  for (const [, mesh] of host.meshes) {
    const card = mesh.userData.cardInstance;
    if (!card) continue;
    if (!crossController && card.rotated !== playerRotated) continue;
    // For cross-controller triggers, route through each card's
    // own controller so scope resolution / log labelling stays
    // correct (Cursed Iron damages bearers under its controller's
    // intent regardless of whose turn just ended).
    const owner = card.rotated ? 'p2' : 'p1';
    const handled = host._resolveSilentTurnTriggerEntries(card, trigger, owner, SILENT_OPS);
    if (handled !== 'all') host.applyCardAutomation(card, [trigger], { controller: owner });
    if (card.equippedCards?.length) {
      for (const eq of card.equippedCards) {
        const eqHandled = host._resolveSilentTurnTriggerEntries(eq, trigger, owner, SILENT_OPS);
        if (eqHandled !== 'all') host.applyCardAutomation(eq, [trigger], { controller: owner });
      }
    }
  }
}

// v1.98. Fire any all-silent entries on `card` for `trigger`. Returns
// 'all' if every matching entry was silent-handled (caller skips the
// prompt pipeline), 'none' otherwise.
export function resolveSilentTurnTriggerEntries(host, card, trigger, player, silentOpSet) {
  // Silenced sources skip their per-turn box text. Returning 'all'
  // tells the caller "every matching entry was handled (by being
  // ignored)" so it doesn't fall through to applyCardAutomation.
  if (isCardSilenced(card) || isDisabled(card)) return 'all';
  const fullCard = host._getFullCard(card.cardId);
  const entries = (fullCard?.automation || []).filter((e) => e.trigger === trigger);
  if (entries.length === 0) return 'all';
  // Auth cutover (Wildfire): the after_each_turn entry is all-silent locally
  // (damage_each + move_aura_to_unvisited_adjacent both dispatch), but the
  // spread is a player CHOICE the server owns. One trigger intent resolves the
  // damage inline and writes the controller's destination cell prompt (or
  // dispels with no fresh ground). Skip the whole local resolution.
  if (host._authZonesEnabled?.()
      && entries.some((e) => (e.effect || []).some((eff) => eff.op === OP.MOVE_AURA_TO_UNVISITED_ADJACENT))) {
    host._sendMatchIntent(triggerIntent(card.id, trigger, {}));
    return 'all';
  }
  // Auth cutover (general). An entry the server owns end to end must never ALSO
  // resolve here: the local dispatch mints client-only ids the server has never
  // seen, and returning 'all' suppresses the trigger intent that would have
  // stamped the server's per-entry firedTriggers guard. The result is a real
  // effect and a phantom duplicate of it (Invasion's second Foot Soldier, which
  // cannot tap, move or attack because every id-keyed authoritative path drops
  // an unknown id). Fall through to applyCardAutomation, whose own delegation
  // gates emit exactly one intent per (source, trigger). The guard then
  // dedupes the PASS_TURN safety net against it.
  //
  // 'none', not a per-entry filter: applyCardAutomation already routes every
  // entry shape correctly, so handing it the whole card is strictly safer than
  // splitting resolution across two owners.
  if (host._authZonesEnabled?.() && entries.some(serverOwnsTurnTriggerEntry)) return 'none';
  // `optional: true` entries carry a rules-text "may." They need the
  // Confirm/Decline drawer, so they are never silent-handled here.
  const allSilent = entries.every((e) => !e.optional && (e.effect || []).every((eff) => silentOpSet.has(eff.op)));
  if (!allSilent) return 'none';
  for (const entry of entries) {
    if (entry.condition && !host._evaluateCondition(entry.condition, card, fullCard)) continue;
    for (const eff of (entry.effect || [])) {
      // OPEN_SCRY_TOP opens a GameBoard-owned UI dialog, so it stays
      // here. Every other silent op delegates to the engine module.
      if (eff.op === OP.OPEN_SCRY_TOP) {
        openScryTopForController(host, player, eff.args || {}, { quiet: true });
        continue;
      }
      dispatchSilentOp(eff, card, entry, host._silentOpsHost, { controller: player });
    }
  }
  return 'all';
}

// v1.50. Codex Start Phase: "Draw a card from either your spellbook
// or atlas. On the first player's first turn of the game, they do not
// draw a card." The choice is the player's; we surface a prompt
// with two options. If exactly one deck has cards left, auto-draw
// from that deck. If both are empty, log and move on. Manual draws
// via context menu / hotkeys remain available for sandbox use, but
// the official Sorcery turn now produces a proper draw prompt.
export function stepDrawCard(host, player) {
  // Manual session: no start-of-turn prompt. The player drags a card off
  // whichever deck they mean to draw from.
  if (!host._automationsEnabled()) return;
  // Auth: the obligation is server state (phase.drawPending), rendered by
  // serverDrawPhaseChoicePrompt. Nothing to push locally.
  if (host._authZonesEnabled?.()) return;
  // Mulligan-before-draw lock. The dice-roll handoff opens the
  // mulligan modal for BOTH players in parallel, and the turn can
  // pass to a player whose mulligan is still 'prompt'/'selecting'.
  // Surfacing the draw prompt now lets them draw before they've
  // resolved the keep/mulligan decision (illegal). Defer until
  // their mulligan finishes; keepHand / executeMulligan replay
  // the deferred prompt for the right player.
  const playerNum = player === 'p2' ? 2 : 1;
  const mulliganPhase = host.state[host._mulliganKeys(playerNum).phase];
  if (mulliganPhase === 'prompt' || mulliganPhase === 'selecting') {
    host._pendingDrawPromptFor = player;
    // v1.105. Persist the deferral so a tab close mid-mulligan still
    // gets the draw prompt back after the mulligan resolves on reload.
    host._openPrompt({
      id: `deferred-draw-${player}`,
      kind: 'deferredDrawPrompt',
      controller: player,
      payload: { player },
    });
    return;
  }
  // Codex: ONLY the very first turn of the GAME skips the draw. Every turn
  // after it (by either player) draws. The rule is about the GLOBAL turn count,
  // not per-player turn numbers. The server tracks `globalTurn` (SET_FIRST_PLAYER
  // → 1, PASS_TURN → +1); sandbox has no server, so fall back to the sum of the
  // per-player counters (the first player is seeded to turn 1, so the sum equals
  // the global turn).
  const turnNumbers = host.state.turnNumbers || {};
  const globalTurn = host.state.globalTurn
    ?? ((turnNumbers.p1 || 0) + (turnNumbers.p2 || 0));
  if (globalTurn === 1) {
    host.logSystem('First turn of the game skips the draw.');
    return;
  }

  const { spellbook, atlas } = ownDrawPiles(host, player);
  const spellbookCount = spellbook?.cards?.length || 0;
  const atlasCount     = atlas?.cards?.length     || 0;

  // v1.104. Always surface the 3-option prompt at start phase.
  // No auto-draw fallback when one deck is empty; the player picks
  // explicitly (or Cancel) every turn. Empty decks just produce a
  // log line when picked. Cancel is provided by the drawer pattern.
  // v1.110. Tag the prompt with the avatar's cardInstance so the
  // StorylinePile / EffectPromptDrawer render the avatar art when the
  // draw is queued behind another effect (instead of the empty text
  // card the null-cardInstance fallback used to produce).
  const avatar = host._findAvatarForPlayer(playerNum);
  const cardName = avatar?.name || (player === 'p1' ? t('Player 1') : t('Player 2'));
  const forbidAtlas = !!resolveAvatarRules(avatar).deckbuilding?.forbidAtlas;
  // v1.105. Stable id keyed by player + turn number so the same
  // prompt rehydrates cleanly on reconnect without colliding with a
  // future turn's prompt. (Date.now() would have made every rehydrate
  // look like a brand new prompt and stacked them up.)
  const turnNumber = (host.state.turnNumbers || {})[player] || 0;
  const promptId = `prompt-start-draw-${player}-t${turnNumber}`;
  // Magician (`forbidAtlas`) has no atlas. Collapse to a single
  // Draw-a-card option pulling from the spellbook. The pile-anchored
  // overlay reads `_singleDraw` to hide the (non-existent) atlas
  // button and relabel the spell button.
  const choices = forbidAtlas
    ? [{ label: t('Draw a Card ({n})', { n: spellbookCount }), value: 'spellbook' }]
    : [
        { label: t('Draw a Spell ({n})', { n: spellbookCount }), value: 'spellbook' },
        { label: t('Draw a Site ({n})', { n: atlasCount }),      value: 'atlas' },
      ];
  host._pushStorylineEvents([{
    id: promptId,
    cardInstance: avatar || null,
    cardName,
    controller: player,
    promptType: 'draw_phase_choice',
    _spellbookPileId: spellbook?.id || null,
    _atlasPileId:     forbidAtlas ? null : (atlas?.id || null),
    _singleDraw:      forbidAtlas,
    choices,
  }]);
  // Persist as a critical prompt so a tab close mid-decision still
  // brings back the Spell/Site/Cancel buttons after reload. Payload
  // is just the player + turn. Rehydrator rebuilds the prompt body
  // from the current pile state (deck counts may have changed).
  host._openPrompt({
    id: promptId,
    kind: 'drawPhaseChoice',
    controller: player,
    payload: { player, turnNumber, promptId },
  });
}

// Rehydrator for the start-phase draw choice. Called by
// _rehydratePromptStack on restore. Re-derives current pile counts
// and pushes the storyline event back onto the queue so the prompt
// drawer renders it. The new entry reuses the persisted id so the
// resolve path closes the descriptor cleanly.
export function rehydrateDrawPhaseChoice(host, descriptor) {
  const { player, promptId } = descriptor.payload || {};
  if (!player || !promptId) return;
  // If the prompt is already in the runtime queue (e.g. restoreSession
  // ran twice in quick succession), don't double-push.
  if ((host.state.pendingPrompts || []).some((p) => p.id === promptId)) return;
  const { spellbook, atlas } = ownDrawPiles(host, player);
  const spellbookCount = spellbook?.cards?.length || 0;
  const atlasCount     = atlas?.cards?.length     || 0;
  const playerNum = player === 'p2' ? 2 : 1;
  const avatar = host._findAvatarForPlayer(playerNum);
  const cardName = avatar?.name || (player === 'p1' ? t('Player 1') : t('Player 2'));
  const forbidAtlas = !!resolveAvatarRules(avatar).deckbuilding?.forbidAtlas;
  const choices = forbidAtlas
    ? [{ label: t('Draw a Card ({n})', { n: spellbookCount }), value: 'spellbook' }]
    : [
        { label: t('Draw a Spell ({n})', { n: spellbookCount }), value: 'spellbook' },
        { label: t('Draw a Site ({n})', { n: atlasCount }),      value: 'atlas' },
      ];
  host._pushStorylineEvents([{
    id: promptId,
    cardInstance: avatar || null,
    cardName,
    controller: player,
    promptType: 'draw_phase_choice',
    _spellbookPileId: spellbook?.id || null,
    _atlasPileId:     forbidAtlas ? null : (atlas?.id || null),
    _singleDraw:      forbidAtlas,
    choices,
  }]);
}

export function stepHealMinions(host, player) {
  // Codex: damage on minions resets at the end of EVERY turn (both
  // players' end-phases), not just the active player's. Avatars are
  // exempt; sites have no HP. Cursed Iron's after_each_turn ping
  // fires AFTER this step in the END phase order, so its damage
  // still lands and persists until the next heal. I.e. through the
  // opponent's start + main phases, then resets when the opponent's
  // end-phase reaches its own heal step.
  //
  // On the auth path the SERVER computes the heal (turn/heal intent) and the
  // opponent renders it from the resulting card-state diff, replacing the
  // per-card MIRROR broadcast below. This client still runs the local loop for
  // its own HUD visuals + the _tookRedirectedDamage clear, but suppresses the
  // per-card stat emit (auth ? skip _broadcastCardStat). End-phase trigger
  // ordering (Cursed Iron lands AFTER the heal) is preserved: the heal intent
  // is sent here, before stepFireTriggers runs the after_each_turn ping.
  const auth = !!host._authZonesEnabled?.();
  if (auth) host._sendMatchIntent?.(healIntent(player));
  //
  // Heal back to the card's live `baseLife` below, NOT the catalog
  // defense. Enchantress' animate-aura transform stamps a fresh
  // baseLife on the picked Aura (= its printed cost), and the
  // catalog Aura row has no defense stat to read from. Using
  // baseLife keeps the heal step correct for both intrinsic
  // minions AND identity-transformed cards.
  for (const [cardId, mesh] of host.meshes) {
    const card = mesh.userData.cardInstance;
    if (!card || card.type === 'Site' || card.type === 'Avatar') continue;
    if (card.currentLife === undefined) continue;
    const baseLife = card.baseLife;
    // Heal to the unit's INTENDED value: printed base plus any manual
    // buff the user has set (HUD +/- edits stamp _manualLifeDelta). A
    // manual buff ABOVE the printed life is static until the user removes
    // it (a 1/1 buffed to 6/6 that took 5 combat damage goes back to 6).
    // A manual edit BELOW it is hand-marked damage and washes like any
    // damage. Same rule as engine/core healStep (the server authority).
    const healed = Math.max(0, (baseLife > 0 ? baseLife : 0) + Math.max(0, card._manualLifeDelta || 0));
    const changed = card.currentLife !== healed;
    card.currentLife = healed;
    // The redirect-death marker (Sir Gawain) washes out with the damage it
    // tracked. A unit that survived a redirected hit must not count as
    // "died from redirected damage" on a later, unrelated death.
    if (card._tookRedirectedDamage) card._tookRedirectedDamage = false;
    // The END phase runs only on the passing player's client, so the
    // heal must MIRROR like any other stat change. Without this the
    // opponent's client keeps the damage until its own pass and the
    // two boards disagree about minion HP for a full turn. Broadcast
    // from the actor (not heal-on-receive at turn:pass) so end-phase
    // trigger damage that intentionally lands AFTER the heal (Cursed
    // Iron's after_each_turn ping) survives in the same order on both
    // clients. Only changed cards send. Usually a handful per turn.
    if (changed && !auth) host._broadcastCardStat(cardId, 'hp', healed);
    const hud = host.lifeHUDs.get(cardId);
    if (hud) {
      const display = card.currentLife + (card._auraStatDelta || 0);
      updateLifeHUD(hud.hpSprite, display, 'hp', display - (card.baseLife ?? display));
    }
    // ATK is NOT reset on turn end.
  }
}

// Codex Start phase step 1: "End all 'Until next turn' effects."
// Owns the Enchantress animate-aura identity revert. The entry was
// queued on the caster's client (applyAnimateAuraTransform), keyed to
// the caster, and only the caster's client runs the caster's start
// phase. So the revert reliably fires on the client that holds the
// snapshot. revertAnimateAuraTransform restores the printed Aura shape;
// detaching the LifeHUD stops the stat pills; card:revert tells the
// opponent to undo their own copy (restored from the card:animate
// snapshot). recomputeStaticAuras lets the reverted aura regain any
// grant-stat / grant-keyword behaviour.
export function stepEndUntilNextTurn(host, player) {
  if (!host._delayedIdentityReverts?.length) return;
  const kept = [];
  for (const r of host._delayedIdentityReverts) {
    if (r.revertPlayer !== player) { kept.push(r); continue; }
    const mesh = host.meshes.get(r.cardId);
    const c = mesh?.userData?.cardInstance;
    if (!c) continue;
    if (r.kind === 'enchantress_aura' && revertAnimateAuraTransform(c)) {
      host._detachLifeHUD(c);
      if (host._authZonesEnabled?.()) host._sendMatchCommand(authCardState.animateCommand(c.id, null));
      host.logAction('{card} reverts to its original form', { card: cardLogLabel(c, msg('Animated aura')) });
    }
  }
  host._delayedIdentityReverts = kept;
  host.recomputeStaticAuras();
}

export function stepPruneExpiredEffects(host, player) {
  // Prune "this turn" ledger entries belonging to the player whose
  // turn just ended (Bloom-site Genesis grants, Black Obelisk
  // start-of-turn mana, etc.). The mirror call on the opponent's
  // client happens via their own end-phase run on their next pass.
  const endingTurnNumber = host.state.turnNumbers[player] || 0;
  host.effectLedger.pruneExpired({ player, turnNumber: endingTurnNumber });
  // Genesis "Provides (E) this turn" site grants (Desert Bloom) stamp the
  // site instance with `_providesThisTurn`; clear it in lockstep with the
  // pruned ledger entries so the temporary site affinity expires too.
  clearProvidesThisTurn(host.meshes);
  // v1.99. Rearm "first per turn" cost-modifier gates so Templar's
  // next Knight/Sir/Dame next turn gets the discount again.
  host.effectLedger.resetCostModifierUses();
  // v1.46. Site-play grants expire at end of turn. Tapping the
  // Avatar grants one site placement; if unused by end of turn,
  // it's gone. (Codex: per-turn ability, not stackable.)
  if (host._siteGrants) host._siteGrants[player] = 0;
  if (host._siteGrantRestriction) host._siteGrantRestriction[player] = null;
  // The Apex of Babel free-cast window expires unused at end of turn.
  if (host._freeCastGrant) host._freeCastGrant[player] = false;
  // v1.115. Avatar of Fire ignite flag expires at end of the
  // installing player's turn. Matches the "this turn" duration in
  // the rules text. Cleared even if the avatar untapped some other
  // way (e.g. a Cloak of Souls effect). The rules-text duration is
  // on the EFFECT, not on the tap state.
  host._clearAvatarOfFireIgnite(player);
  // Per-turn spell-cast history (Sparkmage's "(A) on spells you've
  // cast this turn") expires with the casting player's turn.
  if (host._spellsCastThisTurn) host._spellsCastThisTurn[player] = [];
  // Animist's armed "next magic → Spirit" is a this-turn window; an
  // unused arm expires with the turn (clears the flag, glow, and popup).
  if (host._animistSpiritArm?.[player]) host._disarmAnimistSpirit?.(player);
  // Persecutor's brand expires at end of turn. Brands are only ever
  // applied during the brander's own turn ("Once on your turn"), so
  // clearing every branded card here is exact.
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c || !(c.statuses || []).includes('branded')) continue;
    c.statuses = c.statuses.filter((s) => s !== 'branded');
    host.rebuildStatusBadges(c.id);
    host._broadcastCardStatus(c.id, 'branded', false);
  }
  // Dragonlord's borrowed Dragon identity reverts at the end of the
  // invoking player's turn.
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (c?._dragonIdentityUntilTurnEnd === player) host._revertDragonlordIdentity(c);
  }
  // Death's Door "can't die that turn" immunity expires with THIS
  // turn. For EITHER player (codex: it's usually the attacker's turn
  // when the defender enters DD, and the immunity ends with that very
  // turn, not the DD player's own). Immunity is turn-stamp derived
  // inside PlayerResources, so this only fires the "no longer immune"
  // log line; the actual expiry happens on every client automatically
  // at the turn change, covering after-each-turn triggers too.
  host.playerResources.announceImmunityExpiry();
  // v1.77b. Clear once-per-turn ability flags for cards the
  // ending-turn player controls. The MARK_USED_THIS_TURN op writes
  // into card._abilitiesUsedThisTurn (a Set of string keys); the
  // not_used_this_turn condition reads it. Both players' flags
  // expire on THEIR own turn end. The v1.42 active-vs-non-active
  // rule for triggered abilities doesn't apply here because these
  // are SELF-gated, not stack events.
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c) continue;
    const controller = c.rotated ? 'p2' : 'p1';
    if (controller !== player) continue;
    if (c._abilitiesUsedThisTurn?.size) {
      c._abilitiesUsedThisTurn.clear();
      // The clear must also reach the server (the field is now server-owned),
      // else a reconnect would re-adopt the stale "used" set this turn.
      host._broadcastAbilitiesUsed?.(c);
    }
  }
  // v1.77g. Prune transient automation entries (spell-deployed
  // replacement auras like Enduring Faith). Expire at the end of
  // the opponent's turn that immediately precedes the caster's
  // expiresAtTurn. So "until your next turn" auras last through
  // the opponent's full turn and are gone before the caster's
  // next start phase.
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c?._transientAutomation?.length) continue;
    const kept = [];
    for (const entry of c._transientAutomation) {
      if (!entry.expiresAtTurn || !entry.expiresForPlayer) { kept.push(entry); continue; }
      const owner = entry.expiresForPlayer;
      const opponent = owner === 'p1' ? 'p2' : 'p1';
      const casterCurrentTurn = host.state.turnNumbers[owner] || 0;
      const expireNow = (player === opponent) && (casterCurrentTurn + 1 === entry.expiresAtTurn);
      if (expireNow) continue;
      kept.push(entry);
    }
    if (kept.length !== c._transientAutomation.length) {
      c._transientAutomation = kept;
    }
  }
  // v1.77e. Revert "this turn" stat buffs (Askelon Phoenix-style).
  // Buffs apply to either side's cards but expire at the end of the
  // CURRENT turn regardless of who controls the buffed unit, so we
  // process the whole queue every end-phase.
  if (host._thisTurnStatReverts?.length) {
    for (const { cardId, delta, stat } of host._thisTurnStatReverts) {
      const mesh = host.meshes.get(cardId);
      const c = mesh?.userData?.cardInstance;
      if (!c) continue;
      c.currentAttack = (c.currentAttack ?? c.baseAttack ?? 0) - delta;
      host._broadcastCardStat(cardId, 'atk', c.currentAttack);
      const hud = host.lifeHUDs.get(cardId);
      if (hud?.sprite) {
        const display = c.currentAttack + (c._auraStatDelta || 0);
        updateLifeHUD(hud.sprite, display, 'atk', display - (c.baseAttack ?? display));
      }
      // A 'power' buff raised life too. Undo both.
      if (stat === 'power') {
        c.currentLife = (c.currentLife ?? c.baseLife ?? 0) - delta;
        host._broadcastCardStat(cardId, 'hp', c.currentLife);
        if (hud?.hpSprite) {
          const display = c.currentLife + (c._auraStatDelta || 0);
          updateLifeHUD(hud.hpSprite, display, 'hp', display - (c.baseLife ?? display));
        }
      }
    }
    host._thisTurnStatReverts = [];
  }
  // v1.103. Revert delayed stat buffs that are gated to a specific
  // player's turn counter (Witch curse "-3 power until your next turn").
  if (host._delayedStatReverts?.length) {
    const kept = [];
    for (const r of host._delayedStatReverts) {
      const matchesPlayer = r.revertPlayer === player;
      const reached = (host.state.turnNumbers?.[player] || 0) >= (r.revertAtTurnNumber || 0);
      if (matchesPlayer && reached) {
        const mesh = host.meshes.get(r.cardId);
        const c = mesh?.userData?.cardInstance;
        if (c) {
          c.currentAttack = (c.currentAttack ?? c.baseAttack ?? 0) - r.delta;
          host._broadcastCardStat(r.cardId, 'atk', c.currentAttack);
          const hud = host.lifeHUDs.get(r.cardId);
          if (hud?.sprite) {
            const display = c.currentAttack + (c._auraStatDelta || 0);
            updateLifeHUD(hud.sprite, display, 'atk', display - (c.baseAttack ?? display));
          }
        }
      } else {
        kept.push(r);
      }
    }
    host._delayedStatReverts = kept;
  }
  // v1.102. Revert "this turn" movement bonuses (Bladedancer pattern).
  if (host._thisTurnMovReverts?.length) {
    for (const { cardId, delta } of host._thisTurnMovReverts) {
      const mesh = host.meshes.get(cardId);
      const c = mesh?.userData?.cardInstance;
      if (!c) continue;
      c._movementBonus = Math.max(0, (c._movementBonus || 0) - delta);
      host._broadcastCardStat(cardId, 'mov', c._movementBonus);
      host._refreshMovementHUD(cardId);
    }
    host._thisTurnMovReverts = [];
  }
  // Revert "this turn" granted statuses (Gift of the Serpent's Lethal).
  // Like _thisTurnStatReverts, these expire at the end of the current
  // turn regardless of who controls the card, so the whole queue runs
  // every end-phase. toggleCardStatus broadcasts + rebuilds badges.
  if (host._thisTurnStatusReverts?.length) {
    for (const { cardId, keyword } of host._thisTurnStatusReverts) {
      const mesh = host.meshes.get(cardId);
      const c = mesh?.userData?.cardInstance;
      if (!c || !(c.statuses || []).includes(keyword)) continue;
      host.toggleCardStatus(c, keyword);
    }
    host._thisTurnStatusReverts = [];
  }
  // Revert Wiccan Tools' per-bearer threshold waiver. Like
  // _thisTurnStatusReverts it expires at the end of the current turn
  // regardless of who controls the bearer, so the whole queue runs every
  // end-phase. The paired Spellcaster keyword reverts above.
  if (host._thisTurnThresholdWaiverReverts?.length) {
    for (const { cardId } of host._thisTurnThresholdWaiverReverts) {
      const mesh = host.meshes.get(cardId);
      clearThresholdWaiver(mesh?.userData?.cardInstance);
    }
    host._thisTurnThresholdWaiverReverts = [];
  }
  // Revert "until your next turn" granted statuses (Power of Flight's
  // Airborne). Same expiry gate as _delayedStatReverts: fires at the
  // end of the granter's intervening opponent turn, right before the
  // granter's next turn starts.
  if (host._delayedStatusReverts?.length) {
    const keptStatus = [];
    for (const r of host._delayedStatusReverts) {
      const matchesPlayer = r.revertPlayer === player;
      const reached = (host.state.turnNumbers?.[player] || 0) >= (r.revertAtTurnNumber || 0);
      if (!(matchesPlayer && reached)) { keptStatus.push(r); continue; }
      const mesh = host.meshes.get(r.cardId);
      const c = mesh?.userData?.cardInstance;
      if (c && (c.statuses || []).includes(r.keyword)) host.toggleCardStatus(c, r.keyword);
    }
    host._delayedStatusReverts = keptStatus;
  }
  // v1.60. Wipe transient combat / pick state at end of turn so a
  // half-completed Move-and-Attack or an open attack-target modal
  // can't leak into the opponent's turn. All are local UI state;
  // cheap to clear unconditionally.
  if (host._pendingCombatIntent) host._pendingCombatIntent = null;
  if (host.state.attackTargetModal) host.setState({ attackTargetModal: null });
  // Recompute auras now that THIS_TURN ledger entries (and their
  // _conditional descendants) are gone. Affinity / mana totals,
  // stat deltas, and keyword grants from expired sources need to
  // reflect the post-prune state before the next player's start
  // phase computes their mana provision.
  host.recomputeStaticAuras();
}

export function passTurn(host) {
  if (host.props.isSpectating) return;
  // "Is it my turn to pass?" gate. Online the local seat is pinned by isHost.
  // Solo: the pilot owns BOTH seats, so a pass ALWAYS applies to the seat
  // whose turn it is (currentTurn, folded from the server). Never gate on
  // the piloted perspective (activePlayerNum), which can lag behind the turn
  // (mulligan hand-off, a restored blob) and silently swallowed the pass.
  if (!host.props.isCasualSandbox && host.state.currentTurn !== host._localSeat()) return;
  const outgoingPlayer = host.state.currentTurn;
  // v1.84. Can't end the main phase while a mandatory action is
  // pending. The active player must resolve before passing.
  const blockedBy = host._anyMandatoryActionPending(outgoingPlayer);
  if (blockedBy) { toast.error(t(blockedBy)); return; }
  // The start-phase draw is mandatory (codex: draw a spell or site at
  // the start of your turn). Passing before resolving it forfeited the
  // draw AND wedged the turn machinery. The END phase fired early and
  // a pass was parked behind the very prompt it had just dropped, with
  // no resume path. Block the pass until the active player has drawn.
  // Auth: the obligation is the server's drawPending, not a queued prompt
  // (valkenhall-qa#158); the server would forfeit it at PASS_TURN.
  const undrawn = host._authZonesEnabled?.()
    ? host.state.drawPending?.seat === outgoingPlayer
    : (host.state.pendingPrompts || [])
      .some((p) => p.promptType === 'draw_phase_choice' && p.controller === outgoingPlayer);
  if (undrawn) { toast.error(t('Draw a card before ending your turn.')); return; }
  const nextTurn = outgoingPlayer === 'p1' ? 'p2' : 'p1';
  // Sorcery uses per-player turn counting: only the incoming player's
  // counter advances when a turn starts. The outgoing player's counter
  // stays put (it represents their most recently completed turn).
  const nextTurnNumbers = {
    ...host.state.turnNumbers,
    [nextTurn]: (host.state.turnNumbers[nextTurn] || 0) + 1,
  };
  playSound('uiClick');

  // Drain pending tracker-log bursts so the coalesced life line for
  // the outgoing player's turn lands BEFORE any end-phase log lines.
  host._trackerLogCoalescer.flushAll();

  // ── End phase for the outgoing player ──────────────────
  host._runPhaseSteps(PHASE.END, outgoingPlayer);

  // Forfeit any unresolved start-phase draw the outgoing player
  // never committed to. Without this the entry leaks into the next
  // turn's storyline, where it shows up in the StorylinePile as a
  // dead text card (no card image) and the StartDrawOverlay can't
  // surface buttons for it (they project over the CURRENT player's
  // piles, not the stale owner's).
  const staleDrawIds = (host.state.pendingPrompts || [])
    .filter((p) => p.promptType === 'draw_phase_choice' && p.controller === outgoingPlayer)
    .map((p) => p.id);
  if (staleDrawIds.length > 0) {
    host.setState((s) => ({
      pendingPrompts: dropDrawPhaseChoices(s.pendingPrompts, outgoingPlayer),
    }));
    for (const id of staleDrawIds) host._closePrompt(id);
  }

  // End-of-turn "may" prompts (Thunderstorm's optional one-step move)
  // belong to the OUTGOING player and must resolve during their end
  // step, before the turn passes. In MP the incoming START phase runs
  // on the opponent's client, so the outgoing end-step prompt and the
  // incoming draw prompt never share a screen. In sandbox one client
  // runs both phases synchronously, so a lingering outgoing prompt
  // would coexist with the incoming draw prompt. And two prompts in
  // the storyline bump the pile-anchored draw overlay into the drawer,
  // where it renders decorated with the active avatar's art (looking
  // like a bogus avatar ability). Defer the turn switch until the
  // outgoing player's end-step prompts clear; _maybeResumeDeferredTurnPass
  // completes the pass from the prompt / cell-pick resolution paths.
  // _runPhaseSteps(END) queues its prompts via setState, which Preact
  // applies ASYNCHRONOUSLY. Reading pendingPrompts right here would
  // miss any prompt the end step just pushed (Vatn Draconis's
  // optional submerge landed after the synchronous check, so the pass
  // completed straight into the opponent's draw prompt). An empty
  // setState enqueues this check BEHIND the end-step updates, so the
  // deferral decision sees the settled storyline.
  // The server owns end-phase prompt sequencing under auth (solo included);
  // the empty setState keeps the pass BEHIND any end-step setState flushes.
  host.setState({}, () => {
    completePassTurn(host, nextTurn, nextTurnNumbers);
  });
}

// The tail of a turn pass: run the incoming player's START phase
// (sandbox only. MP runs it on the incoming client), recompute auras,
// and commit the turn switch. Extracted from passTurn so a sandbox
// pass deferred behind the outgoing player's end-of-turn prompts can
// resume here once those prompts resolve.
export function completePassTurn(host, nextTurn, nextTurnNumbers) {
  // Auth match (online AND solo): the authoritative PASS_TURN command owns the
  // pointer flip and the incoming START (run off the diff. See socketListeners
  // foldAndApply; in solo the fold also swaps activePlayerNum to the incoming
  // seat). The END phase already ran in passTurn. Seed the phase once from our
  // synced view (the server's seed-once gate dedupes; either seat may author
  // it), send the command, and await the diff. Flag-off falls through to the
  // legacy local-flip + turn:pass path below.
  if (isAuthMatchEnabled()) {
    host.recomputeStaticAuras();
    // Boundary log lines are derived from the turn-flip diff, not authored
    // here (qa#77 retest; see socketListeners.logTurnBoundaryFromFold).
    if (!host._authPhaseSeeded) {
      sendMatchCommand(host._matchKey(), {
        type: SEED_PHASE,
        payload: {
          matchPhase: host.state.matchPhase,
          currentTurn: host.state.currentTurn,
          turnNumbers: host.state.turnNumbers,
          currentPhase: host.state.currentPhase,
        },
      });
      host._authPhaseSeeded = true;
    }
    sendMatchCommand(host._matchKey(), { type: PASS_TURN, payload: {} });
    return;
  }

  // ── Start phase for the incoming player ────────────────
  // v1.67. In MP, the incoming player's OWN client runs their start
  // phase (via the turn:pass handler) so prompts pop on their screen
  // and engine-driven draws land in their hand.

  // Re-evaluate static auras at turn boundaries. Cheap, and covers
  // subtle drift (e.g. just-restored HP on minions forced a HUD
  // re-render that didn't account for the active aura).
  host.recomputeStaticAuras();

  host.logAction('Ended turn');
  const nextName = host._displayNameForPlayer(nextTurn);
  host.logSystem(turnBanner(nextName, nextTurnNumbers[nextTurn]));
  host.setState({
    currentTurn: nextTurn,
    turnNumbers: nextTurnNumbers,
    currentPhase: PHASE.MAIN,
  });
}

// Resume a sandbox turn pass that was deferred while the outgoing
// player resolved their end-of-turn prompts (Thunderstorm's optional
// move). Called from every prompt / cell-pick resolution path; a no-op
// until both the storyline and any cell-pick are fully clear, so spare
// calls from unrelated resolutions are harmless.
export function maybeResumeDeferredTurnPass(host) {
  const deferred = host._deferredTurnPass;
  if (!deferred) return;
  // A pile multi-select session (Xeraphine's end-of-turn rites) holds
  // the pass open just like a prompt. Resuming mid-pick would pop
  // the opponent's draw prompt over the open dialog.
  if ((host.state.pendingPrompts || []).length > 0
    || host.state.pendingCellTarget
    || host.state.pendingPileSelect) return;
  host._deferredTurnPass = null;
  completePassTurn(host, deferred.nextTurn, deferred.nextTurnNumbers);
}
